import {
  MutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import {
  useIdentifyAccount,
  useIdentifyAnonymous,
  useTrackLogin,
  useTrackLogout,
} from '@app/analytics';
import debounce from 'lodash/debounce';

import { ApiFormattedException } from './exceptions';
import { queryCreate, queryDelete, queryGet, queryUpdate } from './fetch';
import { Consent } from './consents';
import { useCallback } from 'react';
import { AxiosError } from 'axios';
import { Participant } from './participant';
import { getLocale } from '@app/i18n';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

export interface Me {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  locale: string;
  loggedAsOther?: boolean;
  linkedParticipants: string[];
  timezone?: string;
  isSsoAccount: boolean;
  organizations: Array<{
    organizationId: string;
    accountId: string;
    role: 'ADMIN';
  }>;
  deletedAt?: string;
}

export interface Account extends Me {
  consents?: Consent[];
}

/** Query param carrying the page to return to after login. */
export const LOGIN_REDIRECT_PARAM = 'redirect';

/**
 * Returns `value` if it is a safe in-app path to redirect to after login,
 * undefined otherwise (empty, external, protocol-relative or a login page).
 */
export const getSafeRedirectPath = (value?: string | null) => {
  if (
    !value ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.startsWith('/login')
  ) {
    return undefined;
  }
  return value;
};

export interface LogoutOptions {
  /** Do not redirect to the login page after logout. */
  noRedirect?: boolean;
  /**
   * Send the current page along to the login page so the user comes back to
   * it after logging in. Use it for logouts the user did not ask for (expired
   * token), not for a manual logout.
   */
  keepCurrentPage?: boolean;
  onLogout?: () => any;
}

const debouncedLogout = debounce(queryCreate(`/v2/auth/logout`), 1000, {
  leading: true,
});

export const useLogout = ({
  noRedirect,
  keepCurrentPage,
  onLogout: onLogoutCb,
}: LogoutOptions = {}) => {
  const queryClient = useQueryClient();
  const trackLogout = useTrackLogout();

  const onLogout = useCallback(() => {
    if (!noRedirect) {
      const { pathname, search, hash } = window.location;
      const returnTo = keepCurrentPage
        ? getSafeRedirectPath(pathname + search + hash)
        : undefined;
      const params = new URLSearchParams();
      if (returnTo) {
        params.set(LOGIN_REDIRECT_PARAM, returnTo);
      }
      const query = params.toString();
      window.location.replace(query ? `/login?${query}` : '/login');
    }
    setTimeout(() => {
      queryClient.invalidateQueries();
      queryClient.removeQueries();
    }, 200);
    if (onLogoutCb) {
      onLogoutCb();
    }
    trackLogout();
  }, [queryClient, trackLogout, noRedirect, keepCurrentPage, onLogoutCb]);

  return useMutation<void, AxiosError, void>(debouncedLogout, {
    onSuccess: onLogout,
    onError: onLogout,
  });
};

interface CheckEmailResponse {
  exists: boolean;
  method: 'PASSWORD' | 'EMAIL' | 'SSO';
  roles: string[];
}

export const useEmailCheck = () => {
  return useMutation<
    CheckEmailResponse,
    Error,
    { email: string; altcha: string }
  >({
    mutationFn: queryCreate('/v2/auth/check-email'),
  });
};

export const useMe = (allowAnonymous?: boolean) => {
  const queryClient = useQueryClient();
  const { mutate: logout } = useLogout({ keepCurrentPage: true });
  const identity = useIdentifyAccount();
  const identifyAnonymous = useIdentifyAnonymous();

  return useQuery<Me, AxiosError>(['auth', 'me'], queryGet('/v2/auth/me'), {
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(['account', data.id], data);
        identity(data);
      }
    },
    retry: (count, error) => {
      if (error.response && error.response.status === 401) {
        return false;
      }
      return count < 2;
    },
    onError: () => {
      const prevData = queryClient.getQueryData<Me>(['auth', 'me']);
      if (prevData) {
        queryClient.setQueryData(['account', prevData.id], undefined);
      }
      queryClient.setQueryData(['auth', 'me'], undefined);
      if (!allowAnonymous) {
        logout();
      }
      identifyAnonymous();
    },
  });
};

export const useLogin = (preventTracking?: boolean) => {
  const queryClient = useQueryClient();
  const trackLogin = useTrackLogin();
  return useMutation<
    Account,
    undefined,
    { email: string; password: string; altcha?: string }
  >(queryCreate(`/v2/auth/login`), {
    onSuccess: (data) => {
      queryClient.setQueryData(['account', data.id], data);
      queryClient.setQueryData(['auth', 'me'], data);
      if (!preventTracking) {
        trackLogin(data);
      }
    },
  });
};

export interface SignupDto {
  firstName: string;
  lastName?: string;
  email: string;
  locale?: string;
  timezone?: string;
  altcha: string;
}

export const useSignup = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  return useMutation<Account, undefined, SignupDto>(
    (data: SignupDto) =>
      queryCreate(`/v2/auth/signup`)({
        locale: getLocale(i18n),
        timezone: moment.tz.guess(),
        ...data,
      }),
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['account', data.id], data);
        queryClient.setQueryData(['auth', 'me'], data);
      },
    },
  );
};

export interface SendValidationCodeDto {
  email?: string;
  participantId?: string;
  altcha: string;
}

export const useSendEmailValidationCode = () => {
  return useMutation<{ accountId: string }, undefined, SendValidationCodeDto>(
    queryCreate(`/v2/auth/validation-code`),
  );
};

export const useSendPhoneValidationCode = (id?: string) => {
  return useMutation<{ accountId: string }, undefined, { altcha: string }>(
    queryCreate(`/participants/${id}/send-phone-validation-code`),
  );
};

export const useGenerateValidationCode = () => {
  return useMutation<
    { code: string; accountId: string; expireAt: Date },
    undefined,
    void
  >(queryCreate(`/v2/auth/generate-validation-code`));
};

export const useLoginWithEmailValidationCode = () => {
  const queryClient = useQueryClient();
  const trackLogin = useTrackLogin();
  return useMutation<
    Account,
    ApiFormattedException | Error,
    { accountId: string; code: string }
  >(queryCreate(`/v2/auth/validation-code-login`), {
    onSuccess: (data) => {
      queryClient.setQueryData(['account', data.id], data);
      queryClient.setQueryData(['auth', 'me'], data);
      trackLogin(data);
    },
    onError: () => {},
  });
};

export const useVerifyEmailValidationCode = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    Participant,
    ApiFormattedException | Error,
    { accountId: string; code: string }
  >(queryUpdate(`/participants/${id}/validation-code-verify-email`), {
    onSuccess: (data) => {
      queryClient.setQueryData(['participants', data.id], data);
    },
    onError: () => {},
  });
};

export const useVerifyPhoneValidationCode = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    Participant,
    ApiFormattedException | Error,
    { accountId: string; code: string }
  >(queryUpdate(`/participants/${id}/verify-phone`), {
    onSuccess: (data) => {
      queryClient.setQueryData(['participants', data.id], data);
    },
    onError: () => {},
  });
};

export const useSendForgottenPasswordEmail = () => {
  return useMutation<void, undefined, { email: string }>(
    queryCreate(`/v2/auth/forgotten-password`),
  );
};

export const useResetPassword = () => {
  return useMutation<void, undefined, { token: string; password: string }>(
    queryCreate(`/v2/auth/reset-password`),
  );
};

export const useChangePassword = (id?: string) => {
  return useMutation<
    void,
    undefined,
    { previousPassword: string; password: string }
  >(queryUpdate(`/v2/accounts/${id}/change-password`));
};

export const useUpdateAccount = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation(queryUpdate(`/v2/accounts/${id}`), {
    onSuccess: (newValue: Account) => {
      queryClient.setQueriesData(['auth', 'me'], newValue);
      queryClient.setQueriesData(['accounts', newValue.id], newValue);
    },
  });
};

export const useDeleteParticipantAccount = (options?: MutationOptions) => {
  const { mutate: logout } = useLogout();
  const { data: me } = useMe();

  return useMutation(
    async () => {
      const participants = me?.linkedParticipants || [];
      for (const pId of participants) {
        await queryDelete(`/participants/${pId}`)();
      }
    },
    {
      onSuccess: () => logout(),
      ...options,
    },
  );
};

export const useAccountById = (
  id?: string,
  options: UseQueryOptions<void, unknown, Account> = {},
) => {
  return useQuery<void, unknown, Account>(
    ['accounts', id],
    queryGet(`/v2/accounts/${id}`),
    {
      enabled: !!id,
      ...options,
    },
  );
};

export const useIsAdmin = () => {
  const { data: account } = useMe(true);
  return account && account.roles.includes('ADMIN');
};

export const useIsBeta = () => {
  const { data: account } = useMe(true);
  return account && account.roles.includes('BETA');
};

export const useIsClient = () => {
  const { data: account } = useMe(true);
  return account && account.roles.includes('CLIENT');
};

export const useIsParticipant = () => {
  const { data: account } = useMe(true);
  return account && account.roles.includes('PARTICIPANT');
};

export const useCurrentParticipantId = (allowAnonymous?: boolean) => {
  const { data: me } = useMe(allowAnonymous);

  /**
   * After changing the model to have OneToOne relationship between Account and Participant
   * we will be able to remove this logic
   */
  return me && me.linkedParticipants?.length > 0
    ? me.linkedParticipants[0]
    : undefined;
};

export const useLinkedinLogin = () => {
  return useQuery<{ redirectUrl: string }>(
    ['v2', 'linkedin', 'login'],
    queryGet(`/v2/linkedin/login`),
  );
};
